<script setup lang="ts">
import { computed, ref } from 'vue';
import { useOpportunities, useThemeOverrides, useStringFunctions } from '@apparatix/composables';
import { useAccountStore } from '@apparatix/stores/useAccountStore';
import DataTable, { DataTableRowClickEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import ToggleSwitch from 'primevue/toggleswitch';
import Badge from 'primevue/badge';
import { Section, Typography } from '@apparatix/library/core';
import type { ForwardNavigable } from './types';
import ConfigGate from '@apparatix/library/ConfigGate.vue';

const emit = defineEmits<ForwardNavigable>();

const store = useAccountStore();
const { datatable } = useThemeOverrides();
const { formatAsCurrency } = useStringFunctions();

const showAllCampaigns = ref(false);
const visibleCampaigns = computed(() => {
  if (showAllCampaigns.value) return store.accountDeals.campaigns;
  return store.accountDeals.campaigns.filter(campaign => campaign.isActive);
});

const showAllDocuments = ref(false);
const visibleDocuments = computed(() => {
  if (showAllDocuments.value) return store.accountDeals.documents;
  return store.accountDeals.documents.filter(document => document.isActive);
});

const showAllContracts = ref(false);
const visibleContracts = computed(() => {
  if (showAllContracts.value) return store.accountDeals.contracts;
  return store.accountDeals.contracts.filter(contract => contract.isRecent);
});

const showAllInvoices = ref(false);
const visibleInvoices = computed(() => {
  if (showAllInvoices.value) return store.accountDeals.openInvoices;
  return store.accountDeals.openInvoices.filter(contract => ! contract.isPaid);
});

// todo: This currently has to load all leads and opportunities since we need the swimlanes and options from them. It would be nice to be able to split this out to a smaller API call.
const helper = useOpportunities();
helper.loadInitialState();

const onRowClick = (e: DataTableRowClickEvent) => {
  emit('navigate-forward', { view: e.data.type, id: e.data.id, tabId: 'deals' });
};

const onDocRowClick = (e: DataTableRowClickEvent) => {
  emit('navigate-forward', { view: 'document', id: e.data.id, tabId: 'deals' });
};

const rowClass = () => 'cursor-pointer';

</script>
<template>
  <div class="w-full flex flex-col gap-6">
    <ConfigGate enable-for="useCrmCampaigns">
      <Section class="mb-3" :footer="false">
        <DataTable
          :value="visibleCampaigns"
          table-style="min-width: 50rem;"
          :dt="datatable.noLeftPadding"
          class="no-left-padding"
          :row-class="rowClass"
          @row-click="onRowClick"
        >
          <template #header>
            <div class="flex items-center justify-between gap-4">
              <Typography variant="section-heading">
                Leads &#38; Opportunities
                <Badge :value="visibleCampaigns.length" severity="secondary" />
              </Typography>
              <div class="flex items-center gap-2">
                Show All
                <ToggleSwitch v-model="showAllCampaigns" />
              </div>
            </div>
          </template>
          <template #empty>
            <p v-show="showAllCampaigns">
              No leads or opportunities exist for this advertiser.
            </p>
            <p v-show="! showAllCampaigns">
              No active leads or opportunities exist for this advertiser.
            </p>
          </template>
          <template v-if="visibleCampaigns.length > 0">
            <Column
              field="name"
              header="Name"
              sortable
              class="text-[var(--text-link-primary)] cursor-pointer"
            />
            <Column field="status" header="Status" sortable />
            <Column field="budgetDescription" header="Budget Details" sortable />
            <Column
              field="createdDate"
              header="Created"
              sortable
              :sort-field="'age'"
            />
            <Column field="age" header="Age (Days)" sortable />
          </template>
        </DataTable>
      </Section>
    </ConfigGate>
    <Section class="mb-3" :footer="false">
      <DataTable
        :value="visibleDocuments"
        :dt="datatable.noLeftPadding"
        class="no-left-padding"
        table-style="min-width: 50rem;"
        :row-class="rowClass"
        @row-click="onDocRowClick"
      >
        <template #header>
          <div class="flex items-center justify-between gap-4">
            <Typography variant="section-heading">
              Documents
              <Badge :value="visibleDocuments.length" severity="secondary" />
            </Typography>
            <div class="flex items-center gap-2">
              Show All
              <ToggleSwitch v-model="showAllDocuments" />
            </div>
          </div>
        </template>
        <template #empty>
          <p v-show="showAllDocuments">
            No documents exist for this advertiser.
          </p>
          <p v-show="! showAllDocuments">
            No active documents exist for this advertiser.
          </p>
        </template>
        <template v-if="visibleDocuments.length > 0">
          <Column field="name" header="Name" sortable>
            <template #body="slotProps">
              <a
                v-show="slotProps.data.contractId === 0"
                :href="`/apx/proposalTool/index-continue.html?advertiser=${store.accountDetails.id.toString()}&tagid=&pid=${slotProps.data.id}`"
                target="_blank"
                rel="noopener noreferrer"
                class="text-[var(--text-link-primary)]"
              >
                {{ slotProps.data.name }}
              </a>
              <a
                v-show="slotProps.data.contractId !== 0"
                :href="`/util/get/getContractPDFfromCFS.php?recordId=${slotProps.data.contractId}`"
                target="_blank"
                rel="noopener noreferrer"
                class="text-[var(--text-link-primary)]"
              >
                {{ slotProps.data.name }}
              </a>
            </template>
          </Column>
          <Column field="status" header="Status" sortable />
          <Column field="campaign" header="Opportunity" sortable />
          <Column field="type" header="Type" sortable />
          <Column field="age" header="Age (Days)" sortable />
          <Column field="totalFaces" header="Total Faces" sortable />
          <Column field="totalValue" header="Total Value" sortable>
            <template #body="{ data }">
              {{ formatAsCurrency(data.totalValue ?? 0) }}
            </template>
          </Column>
        </template>
      </DataTable>
    </Section>
    <Section class="mb-3" :footer="false">
      <DataTable
        :value="visibleContracts"
        :dt="datatable.noLeftPadding"
        class="no-left-padding"
        table-style="min-width: 50rem;"
      >
        <template #header>
          <div class="flex items-center justify-between gap-4">
            <Typography variant="section-heading">
              Contracts
              <Badge :value="visibleContracts.length" severity="secondary" />
            </Typography>
            <div class="flex items-center gap-2">
              Show All
              <ToggleSwitch v-model="showAllContracts" />
            </div>
          </div>
        </template>
        <template #empty>
          <p v-show="showAllContracts">
            No contracts exist for this advertiser.
          </p>
          <p v-show="! showAllContracts">
            No recent contracts exist for this advertiser.
          </p>
        </template>
        <template v-if="visibleContracts.length > 0">
          <Column field="id" header="Contract" sortable>
            <template #body="slotProps">
              <a
                :href="`/util/get/getContractPDFfromCFS.php?recordId=${slotProps.data.id}`"
                target="_blank"
                rel="noopener noreferrer"
                class="text-[var(--text-link-primary)]"
              >
                {{ slotProps.data.id }}
              </a>
            </template>
          </Column>
          <Column field="owner" header="Owner" sortable />
          <Column field="status" header="Status" sortable />
          <Column field="start" header="Start" sortable />
          <Column field="end" header="End" sortable />
          <Column field="venuesRaw" header="Product Types" sortable />
          <Column field="faceCount" header="Total Faces" sortable />
          <Column field="totalValue" header="Total Value" sortable>
            <template #body="{ data }">
              {{ formatAsCurrency(data.totalValue ?? 0) }}
            </template>
          </Column>
        </template>
      </DataTable>
    </Section>
    <Section class="mb-3" :footer="false">
      <DataTable
        :value="visibleInvoices"
        :dt="datatable.noLeftPadding"
        class="no-left-padding"
        table-style="min-width: 50rem;"
      >
        <template #header>
          <div class="flex items-center justify-between gap-4">
            <Typography variant="section-heading">
              Invoices
              <Badge :value="visibleInvoices.length" severity="secondary" />
            </Typography>
            <div class="flex items-center gap-2">
              Show All
              <ToggleSwitch v-model="showAllInvoices" />
            </div>
          </div>
        </template>
        <template #empty>
          <p v-show="showAllInvoices">
            No invoices exist for this advertiser.
          </p>
          <p v-show="! showAllInvoices">
            No open invoices exist for this advertiser.
          </p>
        </template>
        <template v-if="visibleInvoices.length > 0">
          <Column field="id" header="Invoice" sortable>
            <template #body="slotProps">
              <a
                :href="'/apx/invoice/view/'.concat(slotProps.data.id).concat('-').concat(slotProps.data.invoiceKey)"
                target="_blank"
                rel="noopener noreferrer"
                class="text-[var(--text-link-primary)]"
              >
                {{ slotProps.data.id }}
              </a>
            </template>
          </Column>
          <Column field="invoiceDate" header="Invoice Date" sortable />
          <Column field="dueDate" header="Due Date" sortable />
          <Column field="originalBalance" header="Total" sortable>
            <template #body="{ data }">
              {{ formatAsCurrency(data.originalBalance ?? 0) }}
            </template>
          </Column>
          <Column field="balanceDue" header="Balance Due" sortable>
            <template #body="{ data }">
              {{ formatAsCurrency(data.balanceDue ?? 0) }}
            </template>
          </Column>
          <Column field="age" header="Age (days)" sortable>
            <template #body="slotProps">
              <p v-if="slotProps.data.age > 45" class="text-[var(--red-500)]">
                {{ slotProps.data.age }}
              </p>
              <p v-else>
                {{ slotProps.data.age }}
              </p>
            </template>
          </Column>
        </template>
      </DataTable>
    </Section>
  </div>
</template>
